import React, { useCallback, useState } from "react";

import { Popover } from "@mui/material";
import { AddValueButton } from "components/Deal-workspace/Workspace-metadata-components/Cells/AddValueCell";
import { EditableCell } from "components/Deal-workspace/Workspace-metadata-components/Cells/EditableCell";
import { DataItem, SingleCharForm } from "components/deal/Characteristics-bar/Single-char-form/SingeCharForm";
import { useDateFormatSelect, useDealActions } from "store";
import { DealBarCharacteristic } from "store/types";
import { toastify } from "utils/tyostify";

import { formatCharacteristicDisplay, ValueCell } from "./DealTableCells";

interface Props {
  /** The characteristic (with its data) or null when unset. */
  value: DealBarCharacteristic | null;
  dealId: number;
  variant: "link" | "text";
  /** Only admins / team members may open the editor; others see read-only values. */
  canEdit: boolean;
  onSuccess: (updated: DealBarCharacteristic) => void;
}

export const CharacteristicCell: React.FC<Props> = ({ value, dealId, variant, canEdit, onSuccess }) => {
  const { updateCharacteristicDataAction } = useDealActions();
  const dateFormat = useDateFormatSelect();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const display = value ? formatCharacteristicDisplay(value, dateFormat) : null;
  const color = variant === "link" ? "#26a6ae" : "#071f35";

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSubmit = useCallback(
    async (state: DataItem[]) => {
      if (!value) return;
      try {
        const updated = await updateCharacteristicDataAction({
          characteristic_id: value.id,
          deal_id: dealId,
          characteristic_data: state
        });
        handleClose();
        onSuccess(updated);
      } catch (error) {
        // Keep the popover open so the user can retry.
        toastify({ message: (error as Error)?.message || "Failed to update", type: "error" });
      }
    },
    [value, dealId, onSuccess, updateCharacteristicDataAction]
  );

  // Read-only for non-editors: show the value (no pencil / no "+").
  if (!canEdit) return <ValueCell value={display} variant={"text"} />;

  if (!value) return null;

  return (
    <>
      {display === null ? (
        <AddValueButton onClick={handleOpen} />
      ) : (
        <EditableCell value={display} onClick={handleOpen} color={color} />
      )}
      <Popover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <SingleCharForm characteristic={value} deal_id={dealId} onCancel={handleClose} onSubmit={handleSubmit} />
      </Popover>
    </>
  );
};
